import {
    TransactionView,
    TransactionViewSchema,
} from "@common/types/views/transactionViews";
import { Entity } from "@common/types/entity";
import { useClasses } from "@api/class.api";
import { createDefaultTransactionView } from "@components/transactions/views/DefaultTransactionView";
import { useEffect, useMemo } from "react";
import { useEntities } from "../useEntities";
import { useIsGlFirst } from "../useIsGlFirst";
import { omit } from "@common/helpers/objects";
import { WORKSPACE_USER_CACHE_KEYS } from "../useWorkspaceUserCache";
import { useViewStore } from "./useViewStore";

const TRANSACTION_SESSION_FILTER_KEYS = [
    "search",
    "transactionIds",
    "excludedTransactionIds",
    "auditLogFilter",
    "entityId",
    "normalizedDescriptionId",
] as const satisfies ReadonlyArray<keyof TransactionView["filters"]>;

const viewSanitizer = (view?: TransactionView) => {
    if (!view) {
        return undefined;
    }
    if (!view.filters.entityIds || view.filters.entityIds.length === 0) {
        return null;
    }

    return {
        ...view,
        filters: omit(view.filters, [...TRANSACTION_SESSION_FILTER_KEYS]),
    };
};

interface UseTransactionViewStoreOptions {
    defaultView?: TransactionView;
    initialView?: TransactionView;
    initialFilters?: TransactionView["filters"];
    parentFilters?: TransactionView["filters"];
    cacheKey?: WORKSPACE_USER_CACHE_KEYS;
    onInitialFiltersConsumed?: () => void;
    resetFiltersOnMount?: boolean;
}

export function useTransactionViewStore({
    defaultView,
    initialView,
    initialFilters,
    parentFilters,
    cacheKey,
    onInitialFiltersConsumed,
    resetFiltersOnMount,
}: UseTransactionViewStoreOptions = {}) {
    const availableEntities = useEntities();
    const availableEntityIds = useMemo(
        () => availableEntities.map((entity: Entity) => entity.id),
        [availableEntities],
    );
    const hasAnyClass = useClasses().classes.length > 0;
    const { enabled: isGlFirst } = useIsGlFirst();

    const { view, setView } = useViewStore<TransactionView>({
        defaultView:
            defaultView ??
            createDefaultTransactionView({
                entityIds: availableEntityIds,
                hasAnyClass,
                isGlFirst,
            }),
        viewSchema: TransactionViewSchema,
        initialView,
        initialFilters,
        parentFilters,
        cacheKey,
        viewSanitizer,
        onInitialFiltersConsumed,
        resetFiltersOnMount,
    });

    useEffect(() => {
        if (availableEntityIds.length === 0) {
            return;
        }

        setView((prev: TransactionView) => {
            const currentEntityIds = prev.filters.entityIds ?? [];
            if (currentEntityIds.length === 0) {
                return prev;
            }

            const hasStaleIds = currentEntityIds.some(
                (id: number) => !availableEntityIds.includes(id),
            );
            if (!hasStaleIds) {
                return prev;
            }

            const validIds = currentEntityIds.filter((id: number) =>
                availableEntityIds.includes(id),
            );
            return {
                ...prev,
                filters: {
                    ...prev.filters,
                    entityIds:
                        validIds.length > 0 ? validIds : availableEntityIds,
                },
            };
        });
    }, [availableEntityIds, setView]);

    return { view, setView };
}
